import type { ButtonRouteRelative } from '@/components/ui/buttons/useThButton'
import type {
    BlogAuthorRouteFragment,
    BlogCategoryRouteFragment,
    BlogTagRouteFragment,
    CatalogueEngagementRingFilterInput,
    CatalogueSortOrder,
    EngagementRingDesignFeatureLabelCode,
    EngagementRingLinkFragment,
    GemsApi_StoneColour,
    GemsApi_StoneShape,
    GemstoneRouteFragment,
    JewelleryLinkFragment,
    LabGrownRouteFragment,
    LinkDesignStudioProductFragment,
    LinkOrderFragment,
    LinkQuestionnaireFragment,
    MetalCode,
    NonSkuLabGrownDiamondsFilters,
    NonSkuLabGrownDiamondSortInput,
    PostLinkFragment,
    ProductLinkFragment,
    ReadyToGoRingLinkFragment,
    RegionCode,
    StoneClarityCode,
    StoneColorCode,
    StoneCrystalCode,
    StoneFragment,
    StoneGradeCode,
    StoneShapeCode,
    WeddingRingLinkFragment,
} from '@/graphql/api'
import {
    GemsApi_StoneCategory,
    WeddingRingCategoryLabelCode,
    WeddingRingStyleLabelCode,
} from '@/graphql/api'
import {
    ALL,
    type CompoundRoutePart,
    type CompoundRoutePartWithAll,
    engagementRingDesignStyleListPart,
    engagementRingStyleListPart,
    fancyDiamondColourParts,
    gemstoneClarityPart,
    gemstoneColorGradePart,
    gemstoneColorPart,
    gemstoneCrystalPart,
    jewelleryDesignStylesPart,
    jewelleryGemstonePart,
    jewelleryProductStylesPart,
    type JewelleryProductType,
    jewelleryProductTypesListPart,
    jewelleryProductTypesPart,
    labGrownClarityPart,
    labGrownColorPart,
    metalCompoundSlugsListPart,
    metalSlugsListPart,
    metalSlugsPart,
    monolithShapeListPart,
    monolithShapePart,
    ROUTE_BLOG,
    ROUTE_BLOG_AUTHOR,
    ROUTE_BLOG_CATEGORY,
    ROUTE_BLOG_POST,
    ROUTE_BLOG_TAG,
    ROUTE_BOOKING,
    ROUTE_CART,
    type ROUTE_CHECKOUT_DELIVERY,
    type ROUTE_CHECKOUT_PAYMENT,
    type ROUTE_CHECKOUT_REVIEW,
    ROUTE_ENGAGEMENT_RING,
    ROUTE_ENGAGEMENT_RING_GEMSTONE,
    ROUTE_ENGAGEMENT_RING_LAB_GROWN,
    ROUTE_ENGAGEMENT_RING_STONE_RECOMMENDATIONS,
    ROUTE_ENGAGEMENT_RINGS,
    ROUTE_ENGAGEMENT_RINGS_HOME,
    ROUTE_FORGOTTEN_PASSWORD,
    ROUTE_GEMSTONE_SEARCH,
    ROUTE_HOME,
    ROUTE_JEWELLERIES,
    ROUTE_JEWELLERY,
    ROUTE_LAB_GROWN_DIAMOND_SEARCH,
    ROUTE_LAB_GROWN_FANCY_DIAMOND_SEARCH,
    ROUTE_MAGIC_LINK,
    ROUTE_MAGIC_LINK_REQUEST,
    ROUTE_NATURAL_DIAMOND_SEARCH,
    ROUTE_NATURAL_FANCY_DIAMOND_SEARCH,
    ROUTE_NON_SKU_LAB_GROWN_DIAMOND_SEARCH,
    ROUTE_PAGE,
    ROUTE_PROFILE_DESIGN_STUDIO,
    ROUTE_PROFILE_MAKE_PAYMENT,
    ROUTE_PROFILE_MY_ORDERS,
    ROUTE_PROFILE_PRODUCT,
    ROUTE_PROFILE_PRODUCTS,
    ROUTE_QUESTIONNAIRE,
    ROUTE_READY_TO_GO_RING,
    ROUTE_READY_TO_GO_RINGS,
    ROUTE_SIGN_IN,
    ROUTE_WEDDING_RING,
    ROUTE_WEDDING_RINGS,
    ROUTE_WEDDING_RINGS_HOME,
    type RoutePart,
    type RoutePartWithAll,
    type RoutesPartOne,
    type RoutesPartTwo,
    shapeListPart,
    weddingRingCategoriesPart,
    weddingRingStylePart,
} from '@/router/index'
import { encodeFiltersAndStoneIdentifier } from '@/domain/stone'
import type { FancyDiamondSearchFilters } from '@/domain/stone/fancy-diamond'
import type { GemstoneSearchFilters } from '@/domain/stone/gemstone'
import type { DiamondSearchFilters } from '@/domain/stone/diamond'
import { encodeFragments } from '@/helpers/fragment'
import type { RecommendationsCategory } from '@/domain/product/ring/ring-builder'
import { assertNever } from '@/helpers'
import { getRoutingRegion, type RegionPlugin } from '@/plugins/region'
import type { BookingStep } from '@/views/Booking.vue'
import type { Email } from '@/types/email'
import { encodeNonSkuLabGrownDiamondParams } from '@/domain/stone/search/nonsku/lab-grown-diamond'
import { type App, inject } from 'vue'
import { naturalDiamondDefault } from '@/domain/product/ring/naturalDiamondPreset.ts'
import type { IsNever } from '@/types/utils'
import type {
    NonSkuGemstoneParams,
    NonSkuLabGrownParams,
    NonSkuNaturalParams,
    NonSkuParams,
} from '@/views/product/engagement-ring/composables/nonSkuParams.ts'

export function localize(
    route: ButtonRouteRelative,
    region: RegionCode
): ButtonRouteRelative {
    return {
        ...route,
        params: {
            ...route.params,
            region: getRoutingRegion(region),
        },
    }
}

type RouteParamType<
    T extends
        | RoutePart<any, any>
        | RoutePartWithAll<any, any, any>
        | CompoundRoutePart<any, any, any>
        | CompoundRoutePartWithAll<any, any, any, any>,
> = Parameters<T['routeParamGenerator']>[0]

type MetalList = RouteParamType<typeof metalSlugsListPart>
type WeddingRingStyleList = RouteParamType<typeof weddingRingStylePart>
type WeddingRingCategoryList = RouteParamType<typeof weddingRingCategoriesPart>
type EngagementRingStyleList = RouteParamType<
    typeof engagementRingStyleListPart
>
type EngagementRingDesignStyleList = RouteParamType<
    typeof engagementRingDesignStyleListPart
>

type ParamType<T extends string> = T extends `${string}(\\d+)${string}`
    ? number
    : string

type IsOptionalParam<T extends string> = T extends `${string}?` ? true : false

type StripOptionalRegex<T extends string> = T extends `${infer Stripped}?`
    ? Stripped
    : T

type ParamName<TParam extends string> =
    TParam extends `${infer TParamName}(${string})${string}`
        ? TParamName
        : TParam

type Param<T extends string> =
    IsOptionalParam<T> extends true
        ? {
              [K in ParamName<StripOptionalRegex<T>>]?: ParamType<T>
          }
        : {
              [K in ParamName<T>]: ParamType<T>
          }

/**
 * Params of a run of adjacent params, e.g. `:caratWeight(…)ct:suffix(…)?:grande(…)?`.
 * Param regexes must not contain a `:` (no `(?:…)`) or this splits in the
 * wrong place.
 */
type AdjacentParams<T extends string> = T extends `${infer P}:${infer Rest}`
    ? Param<P> & AdjacentParams<Rest>
    : Param<T>

type Params<
    TPath extends string,
    TDelimiter extends '-' | '/' = TPath extends `${string}-:${string}`
        ? '-'
        : '/',
> = OneObject<
    TPath extends ':slug([^/]+)*'
        ? Param<'slug([^/]+)*'>
        : TPath extends `${string}:${infer P}${TDelimiter}${infer Rest}`
          ? Param<P> & Params<Rest, TDelimiter>
          : // params in a row, e.g. `…-diamond:suffix(…)?:grande(…)?`
            TPath extends `${string}:${infer LastBeforeSuffix}:${infer Suffix}`
            ? Param<LastBeforeSuffix> & AdjacentParams<Suffix>
            : TPath extends `${string}:${infer LastOrSuffix}`
              ? Param<LastOrSuffix>
              : never
>

type OneObject<T extends Record<string, any>> = {} & {
    [K in keyof T]: T[K]
}

type RouteTree =
    | {
          readonly name?: string
          readonly path: string
      }
    | {
          readonly name?: string
          readonly path: string
          readonly children: readonly RouteTree[]
      }

type ExtractChildren<T extends RouteTree[]> = {
    [K in keyof T]: T[K] extends {
        readonly children: infer A extends RouteTree[]
    }
        ? ExtractChildren<{
              [Ak in keyof A]: {
                  name: A[Ak]['name']
                  // if parent contains params, provide them to children
                  path: A[Ak]['path'] | T[K]['path']
                  children: A[Ak] extends {
                      readonly children: readonly RouteTree[]
                  }
                      ? A[Ak]['children']
                      : A[Ak]
              }
          }>
        : T[K]
}

type FlattenArray<T extends readonly any[]> = T extends readonly [
    infer F,
    ...infer R,
]
    ? F extends readonly any[]
        ? [...FlattenArray<F>, ...FlattenArray<R>]
        : [F, ...FlattenArray<R>]
    : []

type RouteTreeFlattened =
    | FlattenArray<ExtractChildren<RoutesPartOne>>
    | FlattenArray<ExtractChildren<RoutesPartTwo>>

type RouteObjectsToParams<T extends { name: string; path: string }[]> = {
    [K in T[number] as K['name']]: IsNever<Params<K['path']>> extends false
        ? {
              name: K['name']
              params: Params<K['path']>
              hash?: string
              query?: Record<string, string | number | undefined>
          }
        : {
              name: K['name']
              hash?: string
              query?: Record<string, string | number | undefined>
          }
}

export type RouteParamsTH = RouteObjectsToParams<RouteTreeFlattened>

export type Routes = {
    designStudio: typeof getDesignStudioRoute
    booking: typeof getBookingRoute
    signIn: typeof getSignInRoute
    home: typeof getHomeRoute
    profile: {
        myOrders: typeof getProfileMyOrders
        magicLinkRequest: typeof getMagicLinkRequestRoute
        magicLink: typeof getMagicLinkRoute
        magicLinkBase: typeof getMagicLinkBaseRoute
        products: typeof getProfileProductsRoute
        makePayment: typeof getProfileMakePaymentRoute
        designStudio: typeof getDesignStudioRoute
    }
    forgottenPassword: typeof getForgottenPasswordRoute
    diamondSearch: typeof getDiamondSearchRoute
    gemstoneSearch: typeof getGemstoneSearchRoute
    fancyDiamondSearch: typeof getFancyDiamondSearchRoute
    nonSku: {
        labGrownDiamondSearch: typeof getNonSkuLabGrownDiamondSearchRoute
    }
    weddingRings: typeof getWeddingRingsRoute
    engagementRings: typeof getEngagementRingsRoute
    readyToGoRings: typeof getReadyToGoRingsRoute
    jewelleries: typeof getJewelleriesRoute
    cart: typeof getCartRoute
    ring: {
        product: typeof catalogueProductRoute
        engagement: typeof engagementRingLinkFragmentToRoute
        wedding: typeof weddingRingLinkFragmentToRoute
        readyToGo: typeof readyToGoRingLinkFragmentToRoute
        jewellery: typeof getJewelleryRoute
    }
    ringBuilder: {
        ring: typeof getRingBuilderRingRoute
        engagementRing: typeof getEngagementRingRoute
        recommendations: typeof getRecommendationsRoute
    }
    blog: {
        list: typeof getBlogListRoute
        post: typeof getBlogPostRoute
        author: typeof getBlogAuthorRoute
        category: typeof getBlogCategoryRoute
        tag: typeof getBlogTagRoute
    }
    page: typeof getPageRoute
    questionnaire: typeof getQuestionnaireRoute
    checkout: typeof getCheckoutRoute
    localize: (route: ButtonRouteRelative) => ButtonRouteRelative
}

function createRoutesPlugin(regionPlugin: RegionPlugin): Routes {
    // todo this should stick the whole object on the app and only ever inject it
    const { routingRegion } = regionPlugin

    function localizeRouteGetter<T extends (...args: any[]) => any>(
        f: T
    ): (...args: Parameters<T>) => ReturnType<T> {
        return (...args: Parameters<T>): ReturnType<T> => {
            const route = f(...args)

            return {
                ...route,
                params: {
                    ...route.params,
                    region: routingRegion.value,
                },
            }
        }
    }

    return {
        designStudio: localizeRouteGetter(getDesignStudioRoute),
        booking: localizeRouteGetter(getBookingRoute),
        signIn: localizeRouteGetter(getSignInRoute),
        home: localizeRouteGetter(getHomeRoute),
        profile: {
            myOrders: localizeRouteGetter(getProfileMyOrders),
            magicLinkRequest: localizeRouteGetter(getMagicLinkRequestRoute),
            magicLink: localizeRouteGetter(getMagicLinkRoute),
            magicLinkBase: localizeRouteGetter(getMagicLinkBaseRoute),
            products: localizeRouteGetter(getProfileProductsRoute),
            makePayment: localizeRouteGetter(getProfileMakePaymentRoute),
            designStudio: localizeRouteGetter(getDesignStudioRoute),
        },
        forgottenPassword: localizeRouteGetter(getForgottenPasswordRoute),
        diamondSearch: localizeRouteGetter(getDiamondSearchRoute),
        gemstoneSearch: localizeRouteGetter(getGemstoneSearchRoute),
        fancyDiamondSearch: localizeRouteGetter(getFancyDiamondSearchRoute),
        nonSku: {
            labGrownDiamondSearch: localizeRouteGetter(
                getNonSkuLabGrownDiamondSearchRoute
            ),
        },
        weddingRings: localizeRouteGetter(getWeddingRingsRoute),
        engagementRings: localizeRouteGetter(getEngagementRingsRoute),
        readyToGoRings: localizeRouteGetter(getReadyToGoRingsRoute),
        jewelleries: localizeRouteGetter(getJewelleriesRoute),
        ring: {
            product: localizeRouteGetter(catalogueProductRoute),
            engagement: localizeRouteGetter(engagementRingLinkFragmentToRoute),
            wedding: localizeRouteGetter(weddingRingLinkFragmentToRoute),
            readyToGo: localizeRouteGetter(readyToGoRingLinkFragmentToRoute),
            jewellery: localizeRouteGetter(getJewelleryRoute),
        },
        ringBuilder: {
            ring: localizeRouteGetter(getRingBuilderRingRoute),
            engagementRing: localizeRouteGetter(getEngagementRingRoute),
            recommendations: localizeRouteGetter(getRecommendationsRoute),
        },
        localize: localizeRouteGetter((route: ButtonRouteRelative) => route),
        blog: {
            list: localizeRouteGetter(getBlogListRoute),
            post: localizeRouteGetter(getBlogPostRoute),
            author: localizeRouteGetter(getBlogAuthorRoute),
            category: localizeRouteGetter(getBlogCategoryRoute),
            tag: localizeRouteGetter(getBlogTagRoute),
        },
        page: localizeRouteGetter(getPageRoute),
        cart: localizeRouteGetter(getCartRoute),
        questionnaire: localizeRouteGetter(getQuestionnaireRoute),
        checkout: localizeRouteGetter(getCheckoutRoute),
    }
}

const routesKey = '__plugin_routes'

export function useRoutes(): Routes {
    return inject(routesKey) as Routes
}

export function installRoutesPlugin(
    app: App,
    regionPlugin: RegionPlugin
): Routes {
    const routes = createRoutesPlugin(regionPlugin)

    app.provide(routesKey, routes)

    return routes
}

function getDesignStudioRoute(): RouteParamsTH[typeof ROUTE_PROFILE_DESIGN_STUDIO] {
    return {
        name: ROUTE_PROFILE_DESIGN_STUDIO,
    }
}

function getBookingRoute(
    step?: BookingStep
): RouteParamsTH[typeof ROUTE_BOOKING] {
    return {
        name: ROUTE_BOOKING,
        params: { step },
    }
}

function getSignInRoute(): RouteParamsTH[typeof ROUTE_SIGN_IN] {
    return {
        name: ROUTE_SIGN_IN,
    }
}

function getMagicLinkRequestRoute(): RouteParamsTH[typeof ROUTE_MAGIC_LINK_REQUEST] {
    return {
        name: ROUTE_MAGIC_LINK_REQUEST,
    }
}

export type MagicLinkRouteParams = {
    email: Email
    expires: number
    hash: string
}

function getMagicLinkBaseRoute(): RouteParamsTH[typeof ROUTE_MAGIC_LINK] {
    return {
        name: ROUTE_MAGIC_LINK,
    }
}

function getMagicLinkRoute(
    { email, expires, hash }: MagicLinkRouteParams,
    redirectUrl?: string
): RouteParamsTH[typeof ROUTE_MAGIC_LINK] {
    return {
        ...getMagicLinkBaseRoute(),
        query: {
            user: email,
            expires,
            hash,
            redirectUrl,
        },
    }
}

function getForgottenPasswordRoute(): RouteParamsTH[typeof ROUTE_FORGOTTEN_PASSWORD] {
    return {
        name: ROUTE_FORGOTTEN_PASSWORD,
    }
}

function getHomeRoute(): RouteParamsTH[typeof ROUTE_HOME] {
    return {
        name: ROUTE_HOME,
    }
}

function getProfileProductsRoute(
    product?: LinkDesignStudioProductFragment
):
    | RouteParamsTH[typeof ROUTE_PROFILE_PRODUCT]
    | RouteParamsTH[typeof ROUTE_PROFILE_PRODUCTS] {
    if (product) {
        return {
            name: ROUTE_PROFILE_PRODUCT,
            params: { id: product.id },
        }
    } else {
        return {
            name: ROUTE_PROFILE_PRODUCTS,
        }
    }
}

function getProfileMakePaymentRoute(
    orderId: string
): RouteParamsTH[typeof ROUTE_PROFILE_MAKE_PAYMENT] {
    return {
        name: ROUTE_PROFILE_MAKE_PAYMENT,
        params: { orderId },
    }
}

function getProfileMyOrders(
    order?: LinkOrderFragment
): RouteParamsTH[typeof ROUTE_PROFILE_MY_ORDERS] {
    return {
        name: ROUTE_PROFILE_MY_ORDERS,
        query: {
            id: order?.id,
        },
    }
}

function getFancyDiamondSearchRoute(
    category: Extract<
        GemsApi_StoneCategory,
        | typeof GemsApi_StoneCategory.FancyDiamond
        | typeof GemsApi_StoneCategory.LabGrownFancyDiamond
    >,
    colour?: GemsApi_StoneColour | ALL,
    shape?: GemsApi_StoneShape,
    filters?: FancyDiamondSearchFilters,
    page?: number,
    stoneIdentifier?: string
):
    | RouteParamsTH[typeof ROUTE_NATURAL_FANCY_DIAMOND_SEARCH]
    | RouteParamsTH[typeof ROUTE_LAB_GROWN_FANCY_DIAMOND_SEARCH] {
    const routeName = ((
        category: Extract<
            GemsApi_StoneCategory,
            | typeof GemsApi_StoneCategory.FancyDiamond
            | typeof GemsApi_StoneCategory.LabGrownFancyDiamond
        >
    ):
        | typeof ROUTE_NATURAL_FANCY_DIAMOND_SEARCH
        | typeof ROUTE_LAB_GROWN_FANCY_DIAMOND_SEARCH => {
        switch (category) {
            case GemsApi_StoneCategory.FancyDiamond:
                return ROUTE_NATURAL_FANCY_DIAMOND_SEARCH
            case GemsApi_StoneCategory.LabGrownFancyDiamond:
                return ROUTE_LAB_GROWN_FANCY_DIAMOND_SEARCH
            default:
                return assertNever(category, ROUTE_NATURAL_FANCY_DIAMOND_SEARCH)
        }
    })(category)

    if (page === 1) {
        page = undefined
    }

    if (shape && !colour) {
        colour = ALL
    }

    return {
        name: routeName,
        params: {
            ...fancyDiamondColourParts.routeParamGenerator(colour),
            ...shapeListPart.routeParamGenerator(shape),
            page,
        },
        hash: encodeFiltersAndStoneIdentifier(filters, stoneIdentifier),
    }
}

function getGemstoneSearchRoute(
    filters?: GemstoneSearchFilters,
    page?: number,
    stoneIdentifier?: string
): RouteParamsTH[typeof ROUTE_GEMSTONE_SEARCH] {
    if (page === 1) {
        page = undefined
    }

    return {
        name: ROUTE_GEMSTONE_SEARCH,
        params: {
            page,
        },
        hash: encodeFiltersAndStoneIdentifier(filters, stoneIdentifier),
    }
}

function getDiamondSearchRoute(
    category: Extract<
        GemsApi_StoneCategory,
        | typeof GemsApi_StoneCategory.Diamond
        | typeof GemsApi_StoneCategory.LabGrownDiamond
    >,
    shape?: GemsApi_StoneShape | ALL,
    filters?: DiamondSearchFilters,
    page?: number,
    stoneIdentifier?: string
):
    | RouteParamsTH[typeof ROUTE_NATURAL_DIAMOND_SEARCH]
    | RouteParamsTH[typeof ROUTE_LAB_GROWN_DIAMOND_SEARCH] {
    const routeName = ((
        category: Extract<
            GemsApi_StoneCategory,
            | typeof GemsApi_StoneCategory.Diamond
            | typeof GemsApi_StoneCategory.LabGrownDiamond
        >
    ):
        | typeof ROUTE_NATURAL_DIAMOND_SEARCH
        | typeof ROUTE_LAB_GROWN_DIAMOND_SEARCH => {
        switch (category) {
            case GemsApi_StoneCategory.Diamond:
                return ROUTE_NATURAL_DIAMOND_SEARCH
            case GemsApi_StoneCategory.LabGrownDiamond:
                return ROUTE_LAB_GROWN_DIAMOND_SEARCH
            default:
                return assertNever(category, ROUTE_NATURAL_DIAMOND_SEARCH)
        }
    })(category)

    if (page === 1) {
        page = undefined
    }

    return {
        name: routeName,
        params: {
            ...shapeListPart.routeParamGenerator(shape),
            page: page === 1 ? undefined : page,
        },
        hash: encodeFiltersAndStoneIdentifier(filters, stoneIdentifier),
    }
}

function getNonSkuLabGrownDiamondSearchRoute(
    filters?: NonSkuLabGrownDiamondsFilters,
    sort?: NonSkuLabGrownDiamondSortInput,
    stoneId?: string
): RouteParamsTH[typeof ROUTE_NON_SKU_LAB_GROWN_DIAMOND_SEARCH] {
    return {
        name: ROUTE_NON_SKU_LAB_GROWN_DIAMOND_SEARCH,
        hash: encodeNonSkuLabGrownDiamondParams(filters, sort, stoneId),
    }
}

export type WeddingRingsRoute =
    | RouteParamsTH[typeof ROUTE_WEDDING_RINGS]
    | RouteParamsTH[typeof ROUTE_WEDDING_RINGS_HOME]

function getWeddingRingsRoute({
    category,
    metal,
    ringStyle,
    priceMin,
    priceMax,
    page,
    sortBy,
}: {
    category?: WeddingRingCategoryList
    metal?: MetalList
    ringStyle?: WeddingRingStyleList
    priceMin?: number
    priceMax?: number
    page?: number
    sortBy?: CatalogueSortOrder
} = {}): WeddingRingsRoute {
    // 1st option - #fragment
    // 2nd option - #design_features[]=cathedral_setting
    // const hash =stringify(extra, stringifyOptions)

    if (page === 1) {
        page = undefined
    }

    if (ringStyle) {
        metal ||= ALL
    }

    if (metal) {
        category ||= ALL
    }

    const name = category ? ROUTE_WEDDING_RINGS : ROUTE_WEDDING_RINGS_HOME

    const extra: {
        price_min?: number
        price_max?: number
        sort?: string
    } = {}

    if (priceMin) {
        extra.price_min = priceMin
    }
    if (priceMax) {
        extra.price_max = priceMax
    }
    if (sortBy) {
        extra.sort = sortBy.toLowerCase()
    }

    return {
        name,
        params: {
            ...weddingRingCategoriesPart.routeParamGenerator(category),
            ...metalSlugsListPart.routeParamGenerator(metal),
            ...weddingRingStylePart.routeParamGenerator(ringStyle),
            page,
        },
        hash:
            extra && Object.values(extra).filter((v) => !!v).length
                ? `#${encodeFragments(extra)}`
                : undefined,
    }
}

export type EngagementRingsRoute =
    | RouteParamsTH[typeof ROUTE_ENGAGEMENT_RINGS_HOME]
    | RouteParamsTH[typeof ROUTE_ENGAGEMENT_RINGS]

function getEngagementRingsRoute({
    metal,
    ringStyle,
    shape,
    designStyle,
    designFeatures,
    priceMin,
    priceMax,
    page,
    sortBy,
    recommendations,
}: {
    metal?: MetalList
    ringStyle?: EngagementRingStyleList
    shape?: StoneShapeCode | ALL
    designStyle?: EngagementRingDesignStyleList
    designFeatures?: EngagementRingDesignFeatureLabelCode[]
    priceMin?: number
    priceMax?: number
    page?: number
    sortBy?: CatalogueSortOrder
    recommendations?: boolean
}): EngagementRingsRoute {
    if (page === 1) {
        page = undefined
    }

    let name:
        | typeof ROUTE_ENGAGEMENT_RINGS_HOME
        | typeof ROUTE_ENGAGEMENT_RINGS = ROUTE_ENGAGEMENT_RINGS_HOME

    if (designStyle) {
        shape ||= ALL
    }

    if (shape) {
        ringStyle ||= ALL
    }

    if (ringStyle) {
        metal ||= ALL
    }

    if (metal) {
        name = ROUTE_ENGAGEMENT_RINGS
    }

    const extra: {
        design_features?: string | string[]
        price_min?: number
        price_max?: number
        sort?: string
    } = {}

    if (designFeatures && designFeatures.length) {
        extra.design_features = designFeatures.map((item) => item.toLowerCase())
    }

    if (priceMin) {
        extra.price_min = priceMin
    }

    if (priceMax) {
        extra.price_max = priceMax
    }

    if (sortBy) {
        extra.sort = sortBy.toLowerCase()
    }

    return {
        name,
        params: {
            ...metalSlugsListPart.routeParamGenerator(metal),
            ...engagementRingStyleListPart.routeParamGenerator(ringStyle),
            ...monolithShapeListPart.routeParamGenerator(shape),
            ...engagementRingDesignStyleListPart.routeParamGenerator(
                designStyle
            ),
            page,
        },
        query: {
            recommendations: recommendations ? 'yes' : undefined,
        },
        hash:
            extra && Object.values(extra).length
                ? `#${encodeFragments(extra)}`
                : undefined,
    }

    // 1st option - #design_features=cathedral_setting,milgrain

    // 2nd option - #design_features[]=cathedral_setting
    // const hash =stringify(extra, stringifyOptions)
}

function getCartRoute(): RouteParamsTH[typeof ROUTE_CART] {
    return {
        name: ROUTE_CART,
    }
}

export type GetJewelleriesRouteParam = {
    productType?: RouteParamType<typeof jewelleryProductTypesListPart>
    productStyle?: RouteParamType<typeof jewelleryProductStylesPart>
    gemstone?: RouteParamType<typeof jewelleryGemstonePart>
    designStyle?: RouteParamType<typeof jewelleryDesignStylesPart>
    metal?: RouteParamType<typeof metalCompoundSlugsListPart>
    page?: number
    priceMin?: number
    priceMax?: number
    sortBy?: CatalogueSortOrder
}

function getJewelleriesRoute({
    productType,
    productStyle,
    gemstone,
    designStyle,
    metal,
    page,
    priceMin,
    priceMax,
    sortBy,
}: GetJewelleriesRouteParam = {}): RouteParamsTH[typeof ROUTE_JEWELLERIES] {
    const extra: {
        price_min?: number
        price_max?: number
        design_features?: string | string[]
        k?: string
        sort?: string
    } = {}

    if (priceMin) {
        extra.price_min = priceMin
    }

    if (priceMax) {
        extra.price_max = priceMax
    }

    if (sortBy) {
        extra.sort = sortBy.toLowerCase()
    }

    if (metal) {
        designStyle ||= ALL
    }

    if (designStyle) {
        gemstone ||= ALL
    }

    if (gemstone) {
        productStyle ||= ALL
    }

    if (productStyle) {
        productType ||= ALL
    }

    const metalParam = metalCompoundSlugsListPart.routeParamGenerator(metal)

    if (metalParam.hash.k) {
        extra.k = metalParam.hash.k
    }

    return {
        name: ROUTE_JEWELLERIES,
        params: {
            ...jewelleryProductTypesListPart.routeParamGenerator(productType),
            ...jewelleryProductStylesPart.routeParamGenerator(productStyle),
            ...jewelleryGemstonePart.routeParamGenerator(gemstone),
            ...jewelleryDesignStylesPart.routeParamGenerator(designStyle),
            ...metalParam.param,
            page: page === 1 ? undefined : page,
        },
        hash:
            extra && Object.values(extra).length
                ? `#${encodeFragments(extra)}`
                : undefined,
    }
}

function jewelleryToType(ring: JewelleryLinkFragment): JewelleryProductType {
    switch (ring.__typename) {
        case 'CatalogueJewelleryBracelet':
            return 'BRACELET'
        case 'CatalogueJewelleryNecklace':
            return 'NECKLACE'
        case 'CatalogueJewelleryEarrings':
            return 'EARRINGS'
        case 'CatalogueJewelleryRing':
            return 'RING'
        default:
            return assertNever(ring, 'BRACELET')
    }
}

function getJewelleryRoute(
    jewelleryItem: JewelleryLinkFragment
): RouteParamsTH[typeof ROUTE_JEWELLERY] {
    return {
        name: ROUTE_JEWELLERY,
        params: {
            ...metalSlugsPart.routeParamGenerator(
                jewelleryItem.primaryMetal.systemCode
            ),
            ...jewelleryProductTypesPart.routeParamGenerator(
                jewelleryToType(jewelleryItem)
            ),
            slug: jewelleryItem.slug,
            suffix: jewelleryItem.suffixSlug
                ? '-' + jewelleryItem.suffixSlug
                : '',
        },
    }
}

export type ReadyToGoRingsRoute =
    | RouteParamsTH[typeof ROUTE_READY_TO_GO_RINGS]
    | RouteParamsTH['rtsList']

function getReadyToGoRingsRoute(
    metal?: MetalList,
    priceMin?: number,
    priceMax?: number,
    page?: number,
    readyToShip?: boolean,
    sortBy?: CatalogueSortOrder
): ReadyToGoRingsRoute {
    if (metal === ALL) {
        metal = undefined
    }

    const extra: { price_min?: number; price_max?: number; sort?: string } = {}

    if (priceMin) {
        extra.price_min = priceMin
    }

    if (priceMax) {
        extra.price_max = priceMax
    }
    if (sortBy) {
        extra.sort = sortBy.toLowerCase()
    }

    if (readyToShip) {
        return {
            name: 'rtsList',
            params: {
                ...metalSlugsListPart.routeParamGenerator(metal),
                page: page === 1 ? undefined : page,
            },
            hash:
                extra && Object.values(extra).length
                    ? `#${encodeFragments(extra)}`
                    : undefined,
        }
    } else {
        return {
            name: ROUTE_READY_TO_GO_RINGS,
            params: {
                ...metalSlugsListPart.routeParamGenerator(metal),
                page: page === 1 ? undefined : page,
            },
            hash:
                extra && Object.values(extra).length
                    ? `#${encodeFragments(extra)}`
                    : undefined,
        }
    }
}

function getRingBuilderRingRoute(
    ring: EngagementRingLinkFragment | CatalogueEngagementRingFilterInput,
    stone:
        | LabGrownRouteFragment
        | GemstoneRouteFragment
        | StoneFragment
        | NonSkuParams
        | null
):
    | RouteParamsTH[typeof ROUTE_ENGAGEMENT_RING]
    | RouteParamsTH[typeof ROUTE_ENGAGEMENT_RING_GEMSTONE]
    | RouteParamsTH[typeof ROUTE_ENGAGEMENT_RING_LAB_GROWN] {
    if ('__typename' in ring) {
        switch (ring.__typename) {
            case 'CatalogueEngagementRing':
                return getEngagementRingRoute(ring, stone)
            default:
                return assertNever(
                    ring.__typename,
                    getEngagementRingRoute(ring, stone)
                )
        }
    } else {
        return getEngagementRingRoute(ring, stone)
    }
}

function getEngagementRingRoute(
    ring: EngagementRingLinkFragment | CatalogueEngagementRingFilterInput,
    stone:
        | LabGrownRouteFragment
        | GemstoneRouteFragment
        | StoneFragment
        | NonSkuParams
        | null
):
    | RouteParamsTH[typeof ROUTE_ENGAGEMENT_RING]
    | RouteParamsTH[typeof ROUTE_ENGAGEMENT_RING_GEMSTONE]
    | RouteParamsTH[typeof ROUTE_ENGAGEMENT_RING_LAB_GROWN] {
    if (!stone) {
        return engagementRingLinkFragmentToRoute(ring)
    }

    if ('__typename' in stone) {
        switch (stone.__typename) {
            case 'GemsApi_Stone':
                return engagementRingNaturalRoute(ring, stone)
            case 'NonSkuGemstone':
                return engagementRingLinkFragmentGemstoneNonSKURoute(
                    ring,
                    stone
                )
            case 'NonSkuLabGrown':
                return engagementRingLinkFragmentLabGrownNonSKURoute(
                    ring,
                    stone
                )
            default:
                return assertNever(
                    stone,
                    engagementRingLinkFragmentToRoute(ring)
                )
        }
    } else {
        switch (stone.type) {
            case 'gemstone':
                return engagementRingLinkFragmentGemstoneNonSKURoute(
                    ring,
                    stone
                )
            case 'lab-grown':
                return engagementRingLinkFragmentLabGrownNonSKURoute(
                    ring,
                    stone
                )
            case 'natural':
                return engagementRingNaturalRoute(ring, stone)
            default:
                return assertNever(
                    stone,
                    engagementRingLinkFragmentToRoute(ring)
                )
        }
    }
}

function catalogueProductRoute(
    product: ProductLinkFragment
):
    | RouteParamsTH[typeof ROUTE_ENGAGEMENT_RING]
    | RouteParamsTH[typeof ROUTE_WEDDING_RING]
    | RouteParamsTH[typeof ROUTE_READY_TO_GO_RING]
    | RouteParamsTH[typeof ROUTE_JEWELLERY]
    | RouteParamsTH[typeof ROUTE_HOME] {
    switch (product.__typename) {
        case 'CatalogueEngagementRing':
            return engagementRingLinkFragmentToRoute(product)
        case 'CatalogueWeddingRing':
            return weddingRingLinkFragmentToRoute(product)
        case 'CatalogueReadyToGoRing':
            return readyToGoRingLinkFragmentToRoute(product)
        case 'CatalogueJewelleryNecklace':
        case 'CatalogueJewelleryEarrings':
        case 'CatalogueJewelleryBracelet':
        case 'CatalogueJewelleryRing':
            return getJewelleryRoute(product)
        default:
            return assertNever(product, getHomeRoute())
    }
}

function engagementRingLinkFragmentToRoute(
    ring: EngagementRingLinkFragment | CatalogueEngagementRingFilterInput
): RouteParamsTH[typeof ROUTE_ENGAGEMENT_RING] {
    return engagementRingNaturalRoute(ring, null)
}

/**
 * Natural diamond PDP. Without a stone, the default natural preset is used;
 * a specific Gems API diamond is kept in the hash as `sku`.
 */
function engagementRingNaturalRoute(
    ring: EngagementRingLinkFragment | CatalogueEngagementRingFilterInput,
    stone: StoneFragment | NonSkuNaturalParams | null
): RouteParamsTH[typeof ROUTE_ENGAGEMENT_RING] {
    let primaryMetalCode: MetalCode
    let centreStoneShapeCode: StoneShapeCode
    let grande: string | undefined

    if ('__typename' in ring) {
        primaryMetalCode = ring.primaryMetal.systemCode
        centreStoneShapeCode = ring.centreStoneConstraints.shape.systemCode
        grande = ring.nonGrande?.catalogueCode ? '-grande' : undefined
    } else {
        primaryMetalCode = ring.primaryMetalCode
        centreStoneShapeCode = ring.centreStoneShape
        grande = ring.grande ? '-grande' : undefined
    }

    let colorCode: StoneColorCode = naturalDiamondDefault.color
    let clarityCode: StoneClarityCode = naturalDiamondDefault.clarity
    let caratWeight = Number(naturalDiamondDefault.carat)
    let sku: string | null = null

    if (stone && '__typename' in stone) {
        colorCode = stone.colorWithInfo.code as StoneColorCode
        clarityCode = stone.clarityWithInfo.code as StoneClarityCode
        caratWeight = Number(stone.weight)
        sku = stone.identifier
    } else if (stone) {
        colorCode = stone.params.naturalDiamondFilter.color
        clarityCode = stone.params.naturalDiamondFilter.clarity
        caratWeight = Number(stone.params.naturalDiamondFilter.carat)
        sku = stone.params.sku
    }

    return {
        name: ROUTE_ENGAGEMENT_RING,
        params: {
            slug: ring.slug,
            ...metalSlugsPart.routeParamGenerator(primaryMetalCode),
            ...monolithShapePart.routeParamGenerator(centreStoneShapeCode),
            ...labGrownColorPart.routeParamGenerator(colorCode),
            ...labGrownClarityPart.routeParamGenerator(clarityCode),
            caratWeight: caratWeight.toFixed(2),
            suffix: ring.suffixSlug ? '-' + ring.suffixSlug : '',
            grande,
        },
        hash: sku ? `#${encodeFragments({ sku })}` : undefined,
    }
}

function engagementRingLinkFragmentGemstoneNonSKURoute(
    ring: EngagementRingLinkFragment | CatalogueEngagementRingFilterInput,
    stone: GemstoneRouteFragment | NonSkuGemstoneParams
): RouteParamsTH[typeof ROUTE_ENGAGEMENT_RING_GEMSTONE] {
    let centreStoneShapeCode: StoneShapeCode
    let primaryMetalCode: MetalCode
    let colorCode: StoneColorCode
    let crystalCode: StoneCrystalCode
    let colorGrade: StoneGradeCode
    let clarity: StoneClarityCode
    let caratWeight: number
    let grande: string | undefined

    if ('__typename' in ring) {
        primaryMetalCode = ring.primaryMetal.systemCode
        centreStoneShapeCode = ring.centreStoneConstraints.shape.systemCode
        grande = ring.nonGrande?.catalogueCode ? '-grande' : undefined
    } else {
        centreStoneShapeCode = ring.centreStoneShape
        primaryMetalCode = ring.primaryMetalCode
        grande = ring.grande ? '-grande' : undefined
    }

    if ('__typename' in stone) {
        colorCode = stone.color.systemCode
        crystalCode = stone.crystal.systemCode
        colorGrade = stone.colorGrade.systemCode
        clarity = stone.clarity.systemCode
        caratWeight = Number(stone.caratWeight)
    } else {
        colorCode = stone.params.nonSkuGemStoneFilter.color
        crystalCode = stone.params.nonSkuGemStoneFilter.crystal
        colorGrade = stone.params.nonSkuGemStoneFilter.grade
        clarity = stone.params.nonSkuGemStoneFilter.clarity
        caratWeight = Number(stone.params.nonSkuGemStoneFilter.carat)
    }

    return {
        name: ROUTE_ENGAGEMENT_RING_GEMSTONE,
        params: {
            slug: ring.slug,
            ...metalSlugsPart.routeParamGenerator(primaryMetalCode),
            ...gemstoneColorPart.routeParamGenerator(colorCode),
            ...gemstoneCrystalPart.routeParamGenerator(crystalCode),
            ...monolithShapePart.routeParamGenerator(centreStoneShapeCode),
            ...gemstoneColorGradePart.routeParamGenerator(colorGrade),
            ...gemstoneClarityPart.routeParamGenerator(clarity),
            caratWeight: caratWeight.toFixed(2),
            suffix: ring.suffixSlug ? '-' + ring.suffixSlug : '',
            grande,
        },
    }
}

function engagementRingLinkFragmentLabGrownNonSKURoute(
    ring: EngagementRingLinkFragment | CatalogueEngagementRingFilterInput,
    stone: LabGrownRouteFragment | NonSkuLabGrownParams
): RouteParamsTH[typeof ROUTE_ENGAGEMENT_RING_LAB_GROWN] {
    let primaryMetalCode: MetalCode
    let centreStoneShapeCode: StoneShapeCode
    let stoneColorCode: StoneColorCode
    let clarityCode: StoneClarityCode
    let caratWeight: number
    let grande: string | undefined

    if ('__typename' in ring) {
        primaryMetalCode = ring.primaryMetal.systemCode
        centreStoneShapeCode = ring.centreStoneConstraints.shape.systemCode
        grande = ring.nonGrande?.catalogueCode ? '-grande' : undefined
    } else {
        primaryMetalCode = ring.primaryMetalCode
        centreStoneShapeCode = ring.centreStoneShape
        grande = ring.grande ? '-grande' : undefined
    }

    if ('__typename' in stone) {
        stoneColorCode = stone.color.systemCode
        clarityCode = stone.clarity.systemCode
        caratWeight = Number(stone.caratWeight)
    } else {
        stoneColorCode = stone.params.nonSkuLabGrownFilter.color
        clarityCode = stone.params.nonSkuLabGrownFilter.clarity
        caratWeight = Number(stone.params.nonSkuLabGrownFilter.carat)
    }

    return {
        name: ROUTE_ENGAGEMENT_RING_LAB_GROWN,
        params: {
            slug: ring.slug,
            ...metalSlugsPart.routeParamGenerator(primaryMetalCode),
            ...monolithShapePart.routeParamGenerator(centreStoneShapeCode),
            ...labGrownColorPart.routeParamGenerator(stoneColorCode),
            ...labGrownClarityPart.routeParamGenerator(clarityCode),
            caratWeight: caratWeight.toFixed(2),
            suffix: ring.suffixSlug ? '-' + ring.suffixSlug : '',
            grande,
        },
    }
}

function weddingRingLinkFragmentToRoute(
    ring: WeddingRingLinkFragment
): RouteParamsTH[typeof ROUTE_WEDDING_RING] {
    return {
        name: ROUTE_WEDDING_RING,
        params: {
            slug: ring.slug,
            ...metalSlugsPart.routeParamGenerator(ring.primaryMetal.systemCode),
            suffix: ring.suffixSlug ? '-' + ring.suffixSlug : '',
        },
    }
}

function readyToGoRingLinkFragmentToRoute(
    ring: ReadyToGoRingLinkFragment
): RouteParamsTH[typeof ROUTE_READY_TO_GO_RING] {
    return {
        name: ROUTE_READY_TO_GO_RING,
        params: {
            slug: ring.slug,
            ...metalSlugsPart.routeParamGenerator(ring.primaryMetal.systemCode),
        },
    }
}

function getRecommendationsRoute(
    category: RecommendationsCategory
): RouteParamsTH[typeof ROUTE_ENGAGEMENT_RING_STONE_RECOMMENDATIONS] {
    return {
        name: ROUTE_ENGAGEMENT_RING_STONE_RECOMMENDATIONS,
        params: { category },
    }
}

function getBlogAuthorRoute(
    author: BlogAuthorRouteFragment,
    page?: number
): RouteParamsTH[typeof ROUTE_BLOG_AUTHOR] {
    return {
        name: ROUTE_BLOG_AUTHOR,
        params: {
            author: author.slug,
            page: page === 1 ? undefined : page,
        },
    }
}

function getBlogCategoryRoute(
    category: BlogCategoryRouteFragment,
    page?: number
): RouteParamsTH[typeof ROUTE_BLOG_CATEGORY] {
    return {
        name: ROUTE_BLOG_CATEGORY,
        params: {
            slug: category.slug,
            page: page === 1 ? undefined : page,
        },
    }
}

function getBlogTagRoute(
    tag: BlogTagRouteFragment,
    page?: number
): RouteParamsTH[typeof ROUTE_BLOG_TAG] {
    return {
        name: ROUTE_BLOG_TAG,
        params: {
            slug: tag.slug,
            page: page === 1 ? undefined : page,
        },
    }
}

function getBlogListRoute(page?: number): RouteParamsTH[typeof ROUTE_BLOG] {
    return {
        name: ROUTE_BLOG,
        params: {
            page: page === 1 ? undefined : page,
        },
    }
}

function getBlogPostRoute(
    blog: PostLinkFragment
): RouteParamsTH[typeof ROUTE_BLOG_POST] {
    return {
        name: ROUTE_BLOG_POST,
        params: { slug: blog.slug },
    }
}

function getPageRoute(
    slug: string
): RouteParamsTH[typeof ROUTE_HOME] | RouteParamsTH[typeof ROUTE_PAGE] {
    if (slug === '/') {
        return {
            name: ROUTE_HOME,
        }
    }

    const parts = slug.split('/').filter((item) => item !== '')

    return {
        name: ROUTE_PAGE,
        /** @ts-expect-error alabala */
        params: { slug: parts },
    }
}

function getQuestionnaireRoute(
    questionnaire: LinkQuestionnaireFragment,
    query?: Record<string, any>
): RouteParamsTH[typeof ROUTE_QUESTIONNAIRE] {
    return {
        name: ROUTE_QUESTIONNAIRE,
        params: {
            id: questionnaire.id,
        },
        query,
    }
}

export function getCheckoutRoute(
    orderId: number,
    step:
        | typeof ROUTE_CHECKOUT_DELIVERY
        | typeof ROUTE_CHECKOUT_PAYMENT
        | typeof ROUTE_CHECKOUT_REVIEW
):
    | RouteParamsTH[typeof ROUTE_CHECKOUT_DELIVERY]
    | RouteParamsTH[typeof ROUTE_CHECKOUT_PAYMENT]
    | RouteParamsTH[typeof ROUTE_CHECKOUT_REVIEW] {
    return {
        name: step,
        params: {
            orderId,
        },
    }
}

if (import.meta.vitest) {
    const { describe, test, expect } = import.meta.vitest

    describe('engagementRingNaturalRoute', () => {
        const ring: CatalogueEngagementRingFilterInput = {
            slug: 'grace',
            primaryMetalCode: 'PLATINUM',
            centreStoneShape: 'ROUND',
            suffixSlug: '',
        }

        test('Mount only uses the default natural stone', () => {
            expect(engagementRingLinkFragmentToRoute(ring)).toMatchObject({
                name: ROUTE_ENGAGEMENT_RING,
                params: {
                    color: 'g',
                    clarity: 'vs2',
                    caratWeight: '1.00',
                },
                hash: undefined,
            })
        })

        test('Natural preset params', () => {
            expect(
                getEngagementRingRoute(ring, {
                    type: 'natural',
                    params: {
                        naturalDiamondFilter: {
                            carat: '1.5',
                            color: 'DIAMOND_D',
                            clarity: 'DIAMOND_VVS2',
                            shape: 'ROUND',
                        },
                        sku: null,
                    },
                })
            ).toMatchObject({
                name: ROUTE_ENGAGEMENT_RING,
                params: {
                    color: 'd',
                    clarity: 'vvs2',
                    caratWeight: '1.50',
                },
                hash: undefined,
            })
        })

        test('Specific diamond keeps its SKU in the hash', () => {
            expect(
                getEngagementRingRoute(ring, {
                    type: 'natural',
                    params: {
                        naturalDiamondFilter: {
                            carat: '1.02',
                            color: 'DIAMOND_G',
                            clarity: 'DIAMOND_VS1',
                            shape: 'ROUND',
                        },
                        sku: 'ABC1234',
                    },
                }).hash
            ).toBe('#sku=ABC1234')
        })
    })

    describe('getWeddingRingRoute', () => {
        test('No parameters', () => {
            expect(getWeddingRingsRoute({})).toStrictEqual({
                name: ROUTE_WEDDING_RINGS_HOME,
                params: {
                    category: undefined,
                    metal: undefined,
                    ringStyle: undefined,
                    page: undefined,
                },
                hash: undefined,
            })
        })
        test('With Category', () => {
            expect(
                getWeddingRingsRoute({
                    category: WeddingRingCategoryLabelCode.Mens,
                })
            ).toStrictEqual({
                name: ROUTE_WEDDING_RINGS,
                params: {
                    category: 'mens',
                    metal: undefined,
                    ringStyle: undefined,
                    page: undefined,
                },
                hash: undefined,
            })
        })
        test('With Category and Metal', () => {
            expect(
                getWeddingRingsRoute({
                    category: WeddingRingCategoryLabelCode.Mens,
                    metal: 'EIGHTEEN_KARAT_WHITE_GOLD',
                })
            ).toStrictEqual({
                name: ROUTE_WEDDING_RINGS,
                params: {
                    category: 'mens',
                    metal: '18k-white-gold',
                    ringStyle: undefined,
                    page: undefined,
                },
                hash: undefined,
            })
        })
        test('With Category, Metal and Style', () => {
            expect(
                getWeddingRingsRoute({
                    category: WeddingRingCategoryLabelCode.Mens,
                    metal: 'EIGHTEEN_KARAT_WHITE_GOLD',
                    ringStyle: WeddingRingStyleLabelCode.Pave,
                })
            ).toStrictEqual({
                name: ROUTE_WEDDING_RINGS,
                params: {
                    category: 'mens',
                    metal: '18k-white-gold',
                    ringStyle: 'pave',
                    page: undefined,
                },
                hash: undefined,
            })
        })
        test('With Style', () => {
            expect(
                getWeddingRingsRoute({
                    ringStyle: 'PAVE',
                })
            ).toStrictEqual({
                name: ROUTE_WEDDING_RINGS,
                params: {
                    category: 'all-categories',
                    metal: 'all-metals',
                    ringStyle: 'pave',
                    page: undefined,
                },
                hash: undefined,
            })
        })
        test('With Metal', () => {
            expect(
                getWeddingRingsRoute({
                    metal: 'EIGHTEEN_KARAT_WHITE_GOLD',
                })
            ).toStrictEqual({
                name: ROUTE_WEDDING_RINGS,
                params: {
                    category: 'all-categories',
                    metal: '18k-white-gold',
                    ringStyle: undefined,
                    page: undefined,
                },
                hash: undefined,
            })
        })
        test('With Metal and Style', () => {
            expect(
                getWeddingRingsRoute({
                    metal: 'EIGHTEEN_KARAT_WHITE_GOLD',
                    ringStyle: 'BEZEL_SET',
                })
            ).toStrictEqual({
                name: ROUTE_WEDDING_RINGS,
                params: {
                    category: 'all-categories',
                    metal: '18k-white-gold',
                    ringStyle: 'bezel_set',
                    page: undefined,
                },
                hash: undefined,
            })
        })
        test('With Category and Style', () => {
            expect(
                getWeddingRingsRoute({
                    category: WeddingRingCategoryLabelCode.Mens,
                    ringStyle: 'BEZEL_SET',
                })
            ).toStrictEqual({
                name: ROUTE_WEDDING_RINGS,
                params: {
                    category: 'mens',
                    metal: 'all-metals',
                    ringStyle: 'bezel_set',
                    page: undefined,
                },
                hash: undefined,
            })
        })
        test('On page 1', () => {
            expect(
                getWeddingRingsRoute({
                    page: 1,
                })
            ).toStrictEqual({
                name: ROUTE_WEDDING_RINGS_HOME,
                params: {
                    category: undefined,
                    metal: undefined,
                    ringStyle: undefined,
                    page: undefined,
                },
                hash: undefined,
            })
        })
        test('On page 2', () => {
            expect(
                getWeddingRingsRoute({
                    page: 2,
                })
            ).toStrictEqual({
                name: ROUTE_WEDDING_RINGS_HOME,
                params: {
                    category: undefined,
                    metal: undefined,
                    ringStyle: undefined,
                    page: 2,
                },
                hash: undefined,
            })
        })
        test('On page 2 with Style', () => {
            expect(
                getWeddingRingsRoute({
                    ringStyle: 'BEZEL_SET',
                    page: 2,
                })
            ).toStrictEqual({
                name: ROUTE_WEDDING_RINGS,
                params: {
                    category: 'all-categories',
                    metal: 'all-metals',
                    ringStyle: 'bezel_set',
                    page: 2,
                },
                hash: undefined,
            })
        })
        test('On page 3 with Metal', () => {
            expect(
                getWeddingRingsRoute({
                    metal: 'EIGHTEEN_KARAT_YELLOW_GOLD',
                    page: 3,
                })
            ).toStrictEqual({
                name: ROUTE_WEDDING_RINGS,
                params: {
                    category: 'all-categories',
                    metal: '18k-yellow-gold',
                    ringStyle: undefined,
                    page: 3,
                },
                hash: undefined,
            })
        })
        test('On page 3 with Category', () => {
            expect(
                getWeddingRingsRoute({
                    category: WeddingRingCategoryLabelCode.Mens,
                    page: 3,
                })
            ).toStrictEqual({
                name: ROUTE_WEDDING_RINGS,
                params: {
                    category: 'mens',
                    metal: undefined,
                    ringStyle: undefined,
                    page: 3,
                },
                hash: undefined,
            })
        })
        test('On page 1 with Category', () => {
            expect(
                getWeddingRingsRoute({
                    category: WeddingRingCategoryLabelCode.Mens,
                    page: 1,
                })
            ).toStrictEqual({
                name: ROUTE_WEDDING_RINGS,
                params: {
                    category: 'mens',
                    metal: undefined,
                    ringStyle: undefined,
                    page: undefined,
                },
                hash: undefined,
            })
        })
        test('With all parameters on page 1', () => {
            expect(
                getWeddingRingsRoute({
                    category: WeddingRingCategoryLabelCode.Mens,
                    metal: 'EIGHTEEN_KARAT_WHITE_GOLD',
                    ringStyle: 'BEZEL_SET',
                    priceMax: 4321.11,
                    priceMin: 1234.44,
                    page: 1,
                })
            ).toStrictEqual({
                name: ROUTE_WEDDING_RINGS,
                params: {
                    category: 'mens',
                    metal: '18k-white-gold',
                    ringStyle: 'bezel_set',
                    page: undefined,
                },
                hash: '#price_min=1234.44&price_max=4321.11',
            })
        })
        test('With all parameters and string design features on page 1', () => {
            expect(
                getWeddingRingsRoute({
                    category: WeddingRingCategoryLabelCode.Mens,
                    metal: 'EIGHTEEN_KARAT_WHITE_GOLD',
                    ringStyle: 'BEZEL_SET',
                    priceMin: 1234.44,
                    priceMax: 4321.11,
                    page: 1,
                })
            ).toStrictEqual({
                name: ROUTE_WEDDING_RINGS,
                params: {
                    category: 'mens',
                    metal: '18k-white-gold',
                    ringStyle: 'bezel_set',
                    page: undefined,
                },
                hash: '#price_min=1234.44&price_max=4321.11',
            })
        })
        test('With all parameters and multiple design features on page 2', () => {
            expect(
                getWeddingRingsRoute({
                    category: WeddingRingCategoryLabelCode.Mens,
                    metal: 'EIGHTEEN_KARAT_WHITE_GOLD',
                    ringStyle: 'BEZEL_SET',
                    priceMin: 1234.44,
                    priceMax: 4321.11,
                    page: 2,
                })
            ).toStrictEqual({
                name: ROUTE_WEDDING_RINGS,
                params: {
                    category: 'mens',
                    metal: '18k-white-gold',
                    ringStyle: 'bezel_set',
                    page: 2,
                },
                hash: '#price_min=1234.44&price_max=4321.11',
            })
        })
        test('With extra parameters only and multiple design features on page 2', () => {
            expect(
                getWeddingRingsRoute({
                    priceMin: 1234.44,
                    priceMax: 4321.11,
                    page: 2,
                })
            ).toStrictEqual({
                name: ROUTE_WEDDING_RINGS_HOME,
                params: {
                    category: undefined,
                    metal: undefined,
                    ringStyle: undefined,
                    page: 2,
                },
                hash: '#price_min=1234.44&price_max=4321.11',
            })
        })
    })

    describe('getEngagementRingsRoute', () => {
        test('No parameters', () => {
            expect(getEngagementRingsRoute({})).toStrictEqual({
                name: ROUTE_ENGAGEMENT_RINGS_HOME,
                params: {
                    metal: undefined,
                    ringStyle: undefined,
                    shape: undefined,
                    designStyle: undefined,
                    page: undefined,
                },
                query: {
                    recommendations: undefined,
                },
                hash: undefined,
            })
        })
        test('With Metal', () => {
            expect(
                getEngagementRingsRoute({
                    metal: 'EIGHTEEN_KARAT_ROSE_GOLD',
                })
            ).toStrictEqual({
                name: ROUTE_ENGAGEMENT_RINGS,
                params: {
                    metal: '18k-rose-gold',
                    ringStyle: undefined,
                    shape: undefined,
                    designStyle: undefined,
                    page: undefined,
                },
                query: {
                    recommendations: undefined,
                },
                hash: undefined,
            })
        })
        test('With Metal and Style', () => {
            expect(
                getEngagementRingsRoute({
                    metal: 'EIGHTEEN_KARAT_ROSE_GOLD',
                    ringStyle: 'GEMSTONE_SIDES',
                })
            ).toStrictEqual({
                name: ROUTE_ENGAGEMENT_RINGS,
                params: {
                    metal: '18k-rose-gold',
                    ringStyle: 'gemstone_sides',
                    shape: undefined,
                    designStyle: undefined,
                    page: undefined,
                },
                query: {
                    recommendations: undefined,
                },
                hash: undefined,
            })
        })
        test('With Metal, Style and Shape', () => {
            expect(
                getEngagementRingsRoute({
                    metal: 'EIGHTEEN_KARAT_ROSE_GOLD',
                    ringStyle: 'GEMSTONE_SIDES',
                    shape: 'ROUND',
                })
            ).toStrictEqual({
                name: ROUTE_ENGAGEMENT_RINGS,
                params: {
                    metal: '18k-rose-gold',
                    ringStyle: 'gemstone_sides',
                    shape: 'round',
                    designStyle: undefined,
                    page: undefined,
                },
                query: {
                    recommendations: undefined,
                },
                hash: undefined,
            })
        })
        test('With Metal, Style, Shape and Design', () => {
            expect(
                getEngagementRingsRoute({
                    metal: 'EIGHTEEN_KARAT_ROSE_GOLD',
                    ringStyle: 'GEMSTONE_SIDES',
                    shape: 'ROUND',
                    designStyle: 'ART_DECO',
                })
            ).toStrictEqual({
                name: ROUTE_ENGAGEMENT_RINGS,
                params: {
                    metal: '18k-rose-gold',
                    ringStyle: 'gemstone_sides',
                    shape: 'round',
                    designStyle: 'art_deco',
                    page: undefined,
                },
                query: {
                    recommendations: undefined,
                },
                hash: undefined,
            })
        })
        test('With Design', () => {
            expect(
                getEngagementRingsRoute({
                    designStyle: 'ART_DECO',
                })
            ).toStrictEqual({
                name: ROUTE_ENGAGEMENT_RINGS,
                params: {
                    metal: 'all-metals',
                    ringStyle: 'all-ring-styles',
                    shape: 'all-shapes',
                    designStyle: 'art_deco',
                    page: undefined,
                },
                query: {
                    recommendations: undefined,
                },
                hash: undefined,
            })
        })
        test('With Shape', () => {
            expect(
                getEngagementRingsRoute({
                    shape: 'ROUND',
                })
            ).toStrictEqual({
                name: ROUTE_ENGAGEMENT_RINGS,
                params: {
                    metal: 'all-metals',
                    ringStyle: 'all-ring-styles',
                    shape: 'round',
                    designStyle: undefined,
                    page: undefined,
                },
                query: {
                    recommendations: undefined,
                },
                hash: undefined,
            })
        })
        test('With Style', () => {
            expect(
                getEngagementRingsRoute({
                    ringStyle: 'GEMSTONE_SIDES',
                })
            ).toStrictEqual({
                name: ROUTE_ENGAGEMENT_RINGS,
                params: {
                    metal: 'all-metals',
                    ringStyle: 'gemstone_sides',
                    shape: undefined,
                    designStyle: undefined,
                    page: undefined,
                },
                query: {
                    recommendations: undefined,
                },
                hash: undefined,
            })
        })
        test('With Style and Design', () => {
            expect(
                getEngagementRingsRoute({
                    ringStyle: 'GEMSTONE_SIDES',
                    designStyle: 'ART_DECO',
                })
            ).toStrictEqual({
                name: ROUTE_ENGAGEMENT_RINGS,
                params: {
                    metal: 'all-metals',
                    ringStyle: 'gemstone_sides',
                    shape: 'all-shapes',
                    designStyle: 'art_deco',
                    page: undefined,
                },
                query: {
                    recommendations: undefined,
                },
                hash: undefined,
            })
        })
        test('On page 1', () => {
            expect(
                getEngagementRingsRoute({
                    page: 1,
                })
            ).toStrictEqual({
                name: ROUTE_ENGAGEMENT_RINGS_HOME,
                params: {
                    metal: undefined,
                    ringStyle: undefined,
                    shape: undefined,
                    designStyle: undefined,
                    page: undefined,
                },
                query: {
                    recommendations: undefined,
                },
                hash: undefined,
            })
        })
        test('On page 2', () => {
            expect(
                getEngagementRingsRoute({
                    page: 2,
                })
            ).toStrictEqual({
                name: ROUTE_ENGAGEMENT_RINGS_HOME,
                params: {
                    metal: undefined,
                    ringStyle: undefined,
                    shape: undefined,
                    designStyle: undefined,
                    page: 2,
                },
                query: {
                    recommendations: undefined,
                },
                hash: undefined,
            })
        })
        test('On page 2 with Shape', () => {
            expect(
                getEngagementRingsRoute({
                    shape: 'ROUND',
                    page: 2,
                })
            ).toStrictEqual({
                name: ROUTE_ENGAGEMENT_RINGS,
                params: {
                    metal: 'all-metals',
                    ringStyle: 'all-ring-styles',
                    shape: 'round',
                    designStyle: undefined,
                    page: 2,
                },
                query: {
                    recommendations: undefined,
                },
                hash: undefined,
            })
        })
        test('On page 3 with Design', () => {
            expect(
                getEngagementRingsRoute({
                    designStyle: 'ART_DECO',
                    page: 3,
                })
            ).toStrictEqual({
                name: ROUTE_ENGAGEMENT_RINGS,
                params: {
                    metal: 'all-metals',
                    ringStyle: 'all-ring-styles',
                    shape: 'all-shapes',
                    designStyle: 'art_deco',
                    page: 3,
                },
                query: {
                    recommendations: undefined,
                },
                hash: undefined,
            })
        })
        test('On page 3 with Metal', () => {
            expect(
                getEngagementRingsRoute({
                    metal: 'EIGHTEEN_KARAT_WHITE_GOLD',
                    page: 3,
                })
            ).toStrictEqual({
                name: ROUTE_ENGAGEMENT_RINGS,
                params: {
                    metal: '18k-white-gold',
                    ringStyle: undefined,
                    shape: undefined,
                    designStyle: undefined,
                    page: 3,
                },
                query: {
                    recommendations: undefined,
                },
                hash: undefined,
            })
        })
        test('On page 1 with Metal', () => {
            expect(
                getEngagementRingsRoute({
                    metal: 'EIGHTEEN_KARAT_WHITE_GOLD',
                    page: 1,
                })
            ).toStrictEqual({
                name: ROUTE_ENGAGEMENT_RINGS,
                params: {
                    metal: '18k-white-gold',
                    ringStyle: undefined,
                    shape: undefined,
                    designStyle: undefined,
                    page: undefined,
                },
                query: {
                    recommendations: undefined,
                },
                hash: undefined,
            })
        })
        test('With all parameters on page 1', () => {
            expect(
                getEngagementRingsRoute({
                    metal: 'EIGHTEEN_KARAT_WHITE_GOLD',
                    ringStyle: 'GEMSTONE_SIDES',
                    shape: 'ROUND',
                    designStyle: 'ART_DECO',
                    designFeatures: ['SPLIT_SHANK'],
                    priceMin: 1234.44,
                    priceMax: 4321.11,
                    page: 1,
                })
            ).toStrictEqual({
                name: ROUTE_ENGAGEMENT_RINGS,
                params: {
                    metal: '18k-white-gold',
                    ringStyle: 'gemstone_sides',
                    shape: 'round',
                    designStyle: 'art_deco',
                    page: undefined,
                },
                query: {
                    recommendations: undefined,
                },
                hash: '#design_features=split_shank&price_min=1234.44&price_max=4321.11',
            })
        })
        test('With all parameters and string design features on page 1', () => {
            expect(
                getEngagementRingsRoute({
                    metal: 'EIGHTEEN_KARAT_WHITE_GOLD',
                    ringStyle: 'GEMSTONE_SIDES',
                    shape: 'ROUND',
                    designStyle: 'ART_DECO',
                    designFeatures: ['SPLIT_SHANK'],
                    priceMin: 1234.44,
                    priceMax: 4321.11,
                    page: 1,
                })
            ).toStrictEqual({
                name: ROUTE_ENGAGEMENT_RINGS,
                params: {
                    metal: '18k-white-gold',
                    ringStyle: 'gemstone_sides',
                    shape: 'round',
                    designStyle: 'art_deco',
                    page: undefined,
                },
                query: {
                    recommendations: undefined,
                },
                hash: '#design_features=split_shank&price_min=1234.44&price_max=4321.11',
            })
        })
        test('With all parameters and multiple design features on page 2', () => {
            expect(
                getEngagementRingsRoute({
                    metal: 'EIGHTEEN_KARAT_WHITE_GOLD',
                    ringStyle: 'GEMSTONE_SIDES',
                    shape: 'ROUND',
                    designStyle: 'ART_DECO',
                    designFeatures: ['SPLIT_SHANK', 'CHANNEL_SETTING'],
                    priceMin: 1234.44,
                    priceMax: 4321.11,
                    page: 2,
                })
            ).toStrictEqual({
                name: ROUTE_ENGAGEMENT_RINGS,
                params: {
                    metal: '18k-white-gold',
                    ringStyle: 'gemstone_sides',
                    shape: 'round',
                    designStyle: 'art_deco',
                    page: 2,
                },
                query: {
                    recommendations: undefined,
                },
                hash: '#design_features=split_shank,channel_setting&price_min=1234.44&price_max=4321.11',
            })
        })
        test('With extra parameters only and multiple design features on page 2', () => {
            expect(
                getEngagementRingsRoute({
                    designFeatures: ['SPLIT_SHANK', 'CHANNEL_SETTING'],
                    priceMin: 1234.44,
                    priceMax: 4321.11,
                    page: 2,
                })
            ).toStrictEqual({
                name: ROUTE_ENGAGEMENT_RINGS_HOME,
                params: {
                    metal: undefined,
                    ringStyle: undefined,
                    shape: undefined,
                    designStyle: undefined,
                    page: 2,
                },
                query: {
                    recommendations: undefined,
                },
                hash: '#design_features=split_shank,channel_setting&price_min=1234.44&price_max=4321.11',
            })
        })
    })

    describe('getReadyToGoRingRoute', () => {
        test('No parameters', () => {
            expect(getReadyToGoRingsRoute()).toStrictEqual({
                name: ROUTE_READY_TO_GO_RINGS,
                params: {
                    metal: undefined,
                    page: undefined,
                },
                hash: undefined,
            })
        })
        test('With Metal', () => {
            expect(getReadyToGoRingsRoute('PLATINUM')).toStrictEqual({
                name: ROUTE_READY_TO_GO_RINGS,
                params: {
                    metal: 'platinum',
                    page: undefined,
                },
                hash: undefined,
            })
        })
        test('On page 1', () => {
            expect(
                getReadyToGoRingsRoute(undefined, undefined, undefined, 1)
            ).toStrictEqual({
                name: ROUTE_READY_TO_GO_RINGS,
                params: {
                    metal: undefined,
                    page: undefined,
                },
                hash: undefined,
            })
        })
        test('On page 2', () => {
            expect(
                getReadyToGoRingsRoute(undefined, undefined, undefined, 2)
            ).toStrictEqual({
                name: ROUTE_READY_TO_GO_RINGS,
                params: {
                    metal: undefined,
                    page: 2,
                },
                hash: undefined,
            })
        })
        test('On page 2 with Metal', () => {
            expect(
                getReadyToGoRingsRoute(
                    'EIGHTEEN_KARAT_WHITE_GOLD',
                    undefined,
                    undefined,
                    2
                )
            ).toStrictEqual({
                name: ROUTE_READY_TO_GO_RINGS,
                params: {
                    metal: '18k-white-gold',
                    page: 2,
                },
                hash: undefined,
            })
        })
        test('With all parameters on page 1', () => {
            expect(
                getReadyToGoRingsRoute(
                    'EIGHTEEN_KARAT_WHITE_GOLD',
                    1234.44,
                    4321.11,
                    1
                )
            ).toStrictEqual({
                name: ROUTE_READY_TO_GO_RINGS,
                params: {
                    metal: '18k-white-gold',
                    page: undefined,
                },
                hash: '#price_min=1234.44&price_max=4321.11',
            })
        })
        test('With extra parameters only on page 2', () => {
            expect(
                getReadyToGoRingsRoute(undefined, 1234.44, 4321.11, 2)
            ).toStrictEqual({
                name: ROUTE_READY_TO_GO_RINGS,
                params: {
                    metal: undefined,
                    page: 2,
                },
                hash: '#price_min=1234.44&price_max=4321.11',
            })
        })
    })
}
